<template>
  <div
    class="h-100"
    v-tinymce="options"
    :key="renderKey"
    @mce-change="(details) => offerSectionSet({ seminarDetails: details })"
    @mce-dirty="() => $emit('dirty')"
  >
    <div
      v-if="offerSections.seminarDetails"
      v-html="offerSections.seminarDetails"
      class="h-100"
    ></div>
    <b-card
      v-else
      class="seminar-details"
    >
      <h5>{{ $t('seminarDetails.heading') }}</h5>
      <b-row>
        <b-col sm="5"><strong>{{ $t('seminarDetails.location') }}</strong></b-col>
        <b-col sm="7">{{ seminarLocation }}</b-col>
      </b-row>
      <b-row v-if="trainerName && trainerName !== placeholder">
        <b-col sm="5"><strong>{{ $t('seminarDetails.trainer') }}</strong></b-col>
        <b-col sm="7">{{ trainerName }}</b-col>
      </b-row>
      <b-row>
        <b-col sm="5"><strong>{{ $t('seminarDetails.date') }}</strong></b-col>
        <b-col sm="7">{{ seminarDate }}</b-col>
      </b-row>
      <b-row>
        <b-col sm="5"><strong>{{ $t('seminarDetails.duration') }}</strong></b-col>
        <b-col sm="7">{{ duration }}<span v-if="offer.id > 4057"> / {{ seminarTimes }}</span></b-col>
      </b-row>
      <b-row>
        <b-col sm="5"><strong>{{ $t('seminarDetails.participants') }}</strong></b-col>
        <b-col
          sm="7"
          v-if="offer && offer.offerType_id !== 4"
        >{{ capacity }}</b-col>
        <b-col
          sm="7"
          v-else
        >{{ offer && offer.numberOfParticipants }}</b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import placeholder from "./placeholder";
import { formatDate, formatDuration, formatStrictCombitDate, formatTimeRange } from "../../misc/LocaleFormat";
import { localizeKnownCombitText } from "../../misc/CombitTextClassifier";

export default {
  name: "SeminarDetails",
  mixins: [placeholder],
  props: {
    options: Object,
  },
  data: function () {
    return {
      renderKey: 0,
    };
  },
  computed: {
    ...mapGetters([
      "offer",
      "offerType",
      "trainer",
      "customer",
      "company",
      "offerSections",
    ]),
    customerCity: function () {
      return this.company ? this.company.city : this.placeholder;
    },
    customerPlace: function () {
      return this.customer && this.customer.company
        ? this.customer.company.city
        : this.placeholder;
    },
    seminarLocation: function () {
      if (this.offerType) {
        if (
          this.offer &&
          this.offer.location &&
          this.offer.location.trim() !== ""
        ) {
          console.log(this.offer);
          return localizeKnownCombitText('location', this.offer.location, this.$t.bind(this));
        }
        // from here probably never used coz location in offer is always set
        // except if the offer is for an open seminar
        if( this.offer && this.offer.offerType_id === 4) {
          console.log('here');
          return this.getOpenSeminarLocation(this.offer);
        }

        let location = "";
        switch (this.offerType.id) {
          case 1: //inhouse
            location = this.$t('combitDefaults.locations.customer', { city: this.customerCity });
            break;
          case 2: //firmen
          // case 4: //offen was already set above
            location = this.$t('combitDefaults.locations.gfu');
            break;
          case 3: //kombi
            location = this.$t('combitDefaults.locations.gfuOrCustomer', { city: this.customerCity });
            break;
          default:
            location = this.placeholder;
            break;
        }
        return location;
      } else {
        return this.placeholder;
      }
    },
    trainerSalutation: function () {
      let title = this.trainer && this.trainer.contact
        ? this.trainer.contact.gender === "m"
          ? this.$t('trainerProfile.maleTitle')
          : ["f", "w"].includes(this.trainer.contact.gender)
            ? this.$t('trainerProfile.femaleTitle')
            : ""
        : this.placeholder;
      if (this.trainer && this.trainer.contact && this.trainer.contact.title) {
        title += ' ' + this.trainer.contact.title;
      }
      return title;
    },
    trainerName: function () {
      if (!this.trainer || !this.trainer.contact) return this.placeholder;
      const name = [
        this.trainerSalutation,
        this.trainer.contact.firstName,
        this.trainer.contact.lastName,
      ].filter(Boolean).join(" ");
      return `${name} — ${this.$t('seminarDetails.trainerInformation')}`;
    },
    seminarDate: function () {
      if (this.offer && this.offer.offerType_id === 4 && this.offer.seminarDateTbd) {
        return this.$t('combitDefaults.dateByArrangement');
      }
      if (this.offer && this.offer.offerType_id === 4) {
        return this.parseSeminarDate(this.offer.seminarDate);
      }
      return this.offer && this.offer.seminarDate
        ? this.localizeSeminarDate(this.offer.seminarDate)
        : this.placeholder;
    },
    duration: function () {
      if (!this.offer || !this.offer.duration) return this.placeholder;
      return formatDuration(
        this.offer.duration,
        this.offer.durationPerDay,
        this.offer.numberOfExecutions,
        this.offer.language || 'de',
        this.$tc.bind(this),
      );
    },
    seminarTimes: function () {
      return formatTimeRange(
        this.offer && this.offer.durationPerDay ? this.offer.startTime : '09:00',
        this.offer && this.offer.durationPerDay ? this.offer.durationPerDay : 7,
        this.$t.bind(this),
      );
    },
    capacity: function () {
      return this.offer && this.offer.capacity
        ? this.offerType && this.offerType.id !== 4
          ? this.$t('seminarDetails.maxParticipants', { count: this.offer.capacity })
          : this.offer.capacity
        : this.placeholder;
    },
  },
  methods: {
    ...mapMutations(["offerSectionSet"]),
    parseSeminarDate(seminarDate) {

      if (!seminarDate) return this.placeholder;

      const startDate = new Date(seminarDate.start.split(' ').join('T'));
      if (parseInt(this.offer.duration <= 1)) {
        return formatDate(startDate, this.offer.language || 'de');
      }
      const endDate = new Date(startDate.getTime());
      endDate.setDate(endDate.getDate() + parseInt(this.offer.duration) - 1);
      return [
        formatDate(startDate, this.offer.language || 'de', { year: undefined }),
        formatDate(endDate, this.offer.language || 'de'),
      ].join(" - ")
    },
    getOpenSeminarLocation(offer) {
      if (parseInt(offer.isRemote) === 1 && parseInt(offer.isClassroom) === 1) {
        return this.$t('combitDefaults.locations.onlineOrGfu');
      }
      if (parseInt(offer.isRemote) === 1) {
        return this.$t('combitDefaults.locations.online');
      }
      if (parseInt(offer.isClassroom) === 1) {
        return this.$t('combitDefaults.locations.gfu');
      }
      return 'not set'
    },
    localizeSeminarDate(value) {
      const knownDefault = localizeKnownCombitText('seminarDate', value, this.$t.bind(this));
      return knownDefault !== value
        ? knownDefault
        : formatStrictCombitDate(value, this.offer.language || 'de');
    },
  },
  watch: {
    offer: {
      handler: function () {
        this.renderKey++;
      },
      deep: true,
    },
    trainer: function () {
      this.renderKey++;
    },
    seminar: function () {
      this.renderKey++;
    },
  },
};
</script>

<style scoped>
.seminar-details {
  height: 100%;
}
</style>
